/**
 * User Management Types
 *
 * Type definitions for user CRUD operations and UI state.
 */

import type { UserStatus, Capability, Environment } from "@tatvex/types";

// ============================================================================
// User Entity Types
// ============================================================================

/** User role assignment */
export interface UserRole {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly capabilities: readonly Capability[];
  readonly isSystem: boolean;
}

/** User entity from API (tenant-specific view) */
export interface TenantUserView {
  readonly userId: string;
  readonly externalId: string;
  readonly email: string;
  readonly name: string;
  readonly status: UserStatus;
  readonly roles: readonly string[];
  readonly environments: readonly Environment[];
  readonly lastLoginAt?: string;
  readonly createdAt: string;
  readonly addedAt: string;
}

/** User list response */
export interface UserListResponse {
  readonly items: readonly TenantUserView[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly hasMore: boolean;
}

/** User invite input */
export interface UserInviteInput {
  readonly email: string;
  readonly name: string;
  readonly roles: readonly string[];
  readonly environments: readonly Environment[];
}

/** User update input */
export interface UserUpdateInput {
  readonly name?: string;
  readonly roles?: readonly string[];
  readonly environments?: readonly Environment[];
}

/** User detail response */
export interface UserDetailResponse {
  readonly user: TenantUserView;
  readonly availableRoles: readonly UserRole[];
  readonly availableEnvironments: readonly Environment[];
}

/** Active session info from API */
export interface SessionInfo {
  readonly id: string;
  readonly userAgent: string | null;
  readonly ipAddress: string | null;
  readonly createdAt: string;
  readonly lastUsedAt: string | null;
  readonly expiresAt: string;
}

/** Session list response from API */
export interface SessionListResponse {
  readonly data: readonly SessionInfo[];
}

// ============================================================================
// UI State Types
// ============================================================================

/** Filter options for user list */
export interface UserListFilters {
  readonly search?: string;
  readonly status?: UserStatus | "all";
  readonly role?: string | "all";
  readonly environment?: Environment | "all";
  readonly page?: number;
  readonly pageSize?: number;
}

/** Tab options for user detail page */
export type UserDetailTab = "profile" | "permissions" | "activity" | "sessions";

/** Confirmation dialog types */
export type UserConfirmationAction = "suspend" | "activate" | "remove" | "resendInvite";

/** User status labels and colors */
export const USER_STATUS_INFO: Record<UserStatus, { label: string; color: string }> = {
  active: { label: "Active", color: "success" },
  suspended: { label: "Suspended", color: "warning" },
  deleted: { label: "Removed", color: "muted" },
};

/** System role info */
export const SYSTEM_ROLE_INFO: Record<string, { label: string; description: string }> = {
  viewer: {
    label: "Viewer",
    description: "Read-only access to configurations and analytics",
  },
  editor: {
    label: "Editor",
    description: "Can edit configurations and products",
  },
  publisher: {
    label: "Publisher",
    description: "Can promote configurations to live",
  },
  admin: {
    label: "Administrator",
    description: "Full access to tenant",
  },
};

/** Environment labels */
export const ENVIRONMENT_INFO: Record<Environment, { label: string; description: string }> = {
  dev: {
    label: "Development",
    description: "Development and testing environment",
  },
  staging: {
    label: "Staging",
    description: "Pre-production staging environment",
  },
  prod: {
    label: "Production",
    description: "Live production environment",
  },
};

/** All available environments */
export const ALL_ENVIRONMENTS: readonly Environment[] = ["dev", "staging", "prod"] as const;

/** All system roles */
export const SYSTEM_ROLES: readonly string[] = ["viewer", "editor", "publisher", "admin"] as const;
